import React, { useState, useEffect, createRef, useRef } from 'react';
import styled from 'styled-components';
import { Icon } from '@zebra-fed/zds-react-desktop';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import { RadioButtonGroup, Item } from "@zebra-fed/zds-react-desktop";
import { CommonButton } from './Button';
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
    toolbarInput: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 20,
        padding: 8
    }
}));

const ToolbarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: ${({ size }) => (size==='medium' ? '270px' : '254px')};
    position: relative;
    @media screen and (max-width: 750px) {
        width: 100%;
    }
`;

const SearchWrapper = styled.div`
    width: 100%;

    .MuiTextField-root {
        width: 100%;
    }
`;

const SearchBoxNComp = styled.div`
    background: white;
    padding: 16px;
    border: 1px solid #e0e3e9;
    border-radius: 4px;
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 100%;
    z-index: 999;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
`;

const SearchTitle = styled.p`
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    color: #333333;
    margin: 0 0 6px 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e0e3e9;
`;

const StyledRadioGroup = styled(RadioButtonGroup)`
    width: 100%;
    .zds-radio-button {
        margin: 8px 0;
    }
`;

export default function Toolbar({ searchText: initialSearchText, multiSearchOpts = [], placeholderText = "Search Name/Email ID:", onSearch, onClear, size='default' }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const radioRef = createRef();
    const searchBoxRef = useRef(null);
    const [searchVal, setSearchVal] = useState(initialSearchText || '');
    const [openSearchBox, setOpenSearchBox] = useState(false);
    const [searchOptVal, setSearchOptVal] = useState(multiSearchOpts[0]?.value || '');

    useEffect(() => {
        setSearchVal(initialSearchText || '');
        if (initialSearchText === '') {
            setSearchOptVal(multiSearchOpts[0]?.value || '');
        }
    }, [initialSearchText, multiSearchOpts]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
                setOpenSearchBox(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function searchTheVal() {
        if (!searchVal.trim()) return;
        onSearch(searchVal, searchOptVal);
        setOpenSearchBox(false);
    }

    function handleKeyPress(e) {
        if (e.keyCode === 13 && searchVal.trim()) {
            onSearch(searchVal, searchOptVal);
            setOpenSearchBox(false);
        }
    }

    function onChangeOptItem(e) {
        setSearchOptVal(e.target.value);
    }

    function handleClearSearch() {
        setSearchVal('');
        onClear('');
        setOpenSearchBox(false);
    }

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchVal(value);
        if (value === '') {
            onClear(value);
        }
        if(value.trim() && multiSearchOpts.length > 1) {
            setOpenSearchBox(true);
        }
    };

    return (
        <ToolbarWrapper ref={searchBoxRef} size={size} >
            <SearchWrapper>
                <TextField
                    inputProps={{className: classes.toolbarInput}}
                    variant='outlined'
                    placeholder={placeholderText}
                    value={searchVal}
                    onFocus={() => multiSearchOpts.length > 1 && setOpenSearchBox(true)}
                    onKeyDownCapture={handleKeyPress}
                    onChange={handleSearchInputChange}
                    InputProps={{
                        startAdornment: <SearchIcon style={{color: '#bdbdbd'}} fontSize='small'/>,
                        endAdornment: searchVal && (
                            <Icon
                                category={'navigation'}
                                name={'close'}
                                size={15}
                                style={{cursor: 'pointer'}}
                                color={'#bdbdbd'}
                                onClick={handleClearSearch}
                            />
                        )
                    }}
                />
                {openSearchBox && multiSearchOpts.length > 1 && (
                    <SearchBoxNComp>
                        <SearchTitle>{t('searchFor')}</SearchTitle>
                        <StyledRadioGroup
                            align="left"
                            ref={radioRef}
                            onChange={onChangeOptItem}
                            value={searchOptVal}
                        >
                            {multiSearchOpts.map((opt, idx) => (
                                <Item
                                    key={idx}
                                    value={opt.value}
                                    defaultChecked={opt.value === searchOptVal}
                                >
                                    {opt.name}
                                </Item>
                            ))}
                        </StyledRadioGroup>
                        <ButtonContainer>
                            <CommonButton
                                type="outlined"
                                onClick={() => setOpenSearchBox(false)}
                                disabled={false}
                            >
                                {t('Cancel')}
                            </CommonButton>
                            <CommonButton
                                type="primary"
                                onClick={searchTheVal}
                                disabled={!searchVal.trim()}
                            >
                                {t('Search')}
                            </CommonButton>
                        </ButtonContainer>
                    </SearchBoxNComp>
                )}
            </SearchWrapper>
        </ToolbarWrapper>
    );
}